<template>
  <Modal :open="internalOpen" :width="1000">
    <template #header>
      <ModalHeader
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </ModalHeader>
    </template>

    <div class="mt-8">
      <FormCard :title="$t(`${tKey}.general.title`)">
        <div class="flex flex-col gap-y-5">
          <InputField
            v-model="rawAdvertisement.name"
            :error="errors?.name"
            type="text"
            :label="$t('advertisement.name')"
            :placeholder="$t('advertisement.name')"
          />

          <Multiselect
            v-model="rawAdvertisement.company"
            :error="errors?.company"
            :searchable="true"
            :label="$t('advertisement.company')"
            :placeholder="$t('advertisement.company')"
            :options="fetchCompanies"
            class="!w-full"
          />

          <div class="flex gap-x-10">
            <Switch v-model="rawAdvertisement.showInApp" :label="$t('advertisement.showInApp')" />
            <Switch v-model="rawAdvertisement.showInForBusiness" :label="$t('advertisement.showInForBusiness')" />
          </div>

          <div class="flex gap-x-5">
            <DatePicker
              v-model="rawAdvertisement.validityStart"
              :error="errors?.validityStart"
              mode="datetime"
              class="w-full"
              :label="$t('advertisement.validityStart')"
            />
            <DatePicker
              v-model="rawAdvertisement.validityEnd"
              :error="errors?.validityEnd"
              mode="datetime"
              class="w-full"
              :label="$t('advertisement.validityEnd')"
            />
          </div>

          <MultiselectTags
            v-model="rawAdvertisement.places"
            :error="errors?.places"
            :options="placeOptions"
            :label="$t('advertisement.places')"
          />

          <MultiselectTags
            v-model="rawAdvertisement.regions"
            :label="$t('pages.pointReward.regions')"
            :options="regionOptions"
            :object="false"
          />
        </div>
      </FormCard>

      <FormCard :title="$t(`${tKey}.translated.title`)">
        <TranslationAccordion
          v-slot="{ translation }"
          v-model="rawAdvertisement"
          :errors="errors"
          :default-values-for-new-translations="{ title: '', description: '', url: '', image: undefined }"
        >
          <div class="flex flex-col gap-y-5">
            <InputField
              v-model="translation.title"
              :error="getTranslationError(translation.language)"
              type="text"
              max-length="50"
              :label="$t('advertisement.title')"
              :placeholder="$t('advertisement.title')"
            />
            <small class="mb-5" :class="{ 'text-red-120': (translation.title?.length ?? 0) > MAX_TITLE_LENGTH }">
              {{ $t('advertisement.fieldLengthHelp', { currentCount: translation.title?.length ?? 0, maxCount: MAX_TITLE_LENGTH }) }}
            </small>
            <InputField
              v-model="translation.description"
              type="text"
              max-length="120"
              :label="$t('advertisement.description')"
              :placeholder="$t('advertisement.description')"
            />
            <small class="mb-5" :class="{ 'text-red-120': (translation.description?.length ?? 0) > MAX_DESCRIPTION_LENGTH }">
              {{ $t('advertisement.fieldLengthHelp', { currentCount: translation.description?.length ?? 0, maxCount: MAX_DESCRIPTION_LENGTH }) }}
            </small>
            <InputField
              v-model="translation.url"
              type="text"
              :label="$t('advertisement.url')"
              :placeholder="$t('advertisement.url')"
            />
            <small class="mb-5" :class="{ 'text-red-120': (/https?:\/\//.test(translation.url) === false) }">
              {{ $t('advertisement.urlHelp') }}
            </small>
            <div>
              <SingleFileUploadMedium
                v-model="translation.image"
                class="h-full"
                :label="$t('advertisement.image')"
                :aspect="9 / 16"
              />
              <small class="mb-5">{{ $t('advertisement.imageHelp') }}</small>
            </div>
          </div>
        </TranslationAccordion>
      </FormCard>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Modal from '@utils/Modal/Modal.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import InputField from '@utils/inputs/Input.vue';
import DatePicker from '@utils/inputs/DatePicker.vue';
import Switch from '@utils/inputs/Switch.vue';
import Multiselect from '@utils/Multiselect.vue';
import MultiselectTags from '@utils/MultiselectTags.vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import {
  array, boolean, date, object, string,
} from 'yup';
import type {
  WbFile, Advertisement, Company, AdvertisementTranslation, Region,
} from '@/entities/Entities';
import { syncRef } from '@/hooks/state';
import { collOptionsProvider, enumOptionsProvider } from '@/components/utils/table/hooks/provider';
import { useNotify } from '@/components/utils/notifications';
import { useValidation } from '@/hooks/form';
import { useFetchAction } from '@/hooks/fetch';
import TranslationAccordion from '@/components/utils/language/TranslationAccordion.vue';
import { Upload } from '@/components/utils/upload/fileUpload';
import SingleFileUploadMedium from '@/components/utils/inputs/SingleFileUploadMedium.vue';

interface Props {
  open: boolean;
  advertisement?: Advertisement;
}

const regionOptions = collOptionsProvider<Region>('/regions', {}, (region: Region) => ({
  label: region.name,
  value: region['@id'],
}));

const emit = defineEmits(['update:open', 'update:advertisement', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalAdvertisement = useVModel(props, 'advertisement', emit);

const isSubmitting = ref(false);

const isNew = computed(() => !props.advertisement);
const tKey = computed(() => (isNew.value ? 'pages.advertisementList.modal.new' : 'pages.advertisementList.modal.edit'));

const MAX_TITLE_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 120;

const { t } = useI18n();

const notify = useNotify();

const initialData = (advertisement?: Advertisement) => {
  const data = advertisement
    ? {
      ...advertisement,
      company: advertisement.company?.['@id'] ?? null,
      places: [
        ...advertisement.places.map((p) => ({
          value: p.spot,
          label: t(`advertisement.spots.${p.spot}`),
          '@id': p?.['@id'] ?? undefined,
        })),
      ],
      regions:
          advertisement.regions && advertisement.regions.length
            ? Array.from(advertisement.regions).map((region: Region) => region['@id'] ?? region.id)
            : [],
      translations: [
        ...advertisement.translations.map((trans) => ({
          ...trans,
          image: trans.image ? Upload.from<WbFile>(trans.image) : undefined,
        })),
      ],
    }
    : {
      name: '',
      company: null,
      showInApp: false,
      showInForBusiness: false,
      validityStart: null,
      validityEnd: null,
      places: [],
      regions: [],
      translations: [
        {
          title: '',
          description: '',
          url: '',
          image: undefined,
          language: 'de',
        },
      ],
    };
  return data;
};

const rawAdvertisement = syncRef(internalAdvertisement, (advertisement) => ({
  ...initialData(advertisement),
}));

const fetchCompanies = collOptionsProvider<Company>(
  '/companies',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
  }),
);

const placeOptions = enumOptionsProvider('/enums/AdvertisementSpot', (value) => ({
  value,
  label: t(`advertisement.spots.${value}`),
}));

const getTranslationError = (language: string) => {
  if (!errors?.value?.translations) return undefined;

  if (typeof errors.value.translations === 'string') {
    return errors.value.translations;
  }

  const translationIndex = rawAdvertisement.value.translations.findIndex((t) => t.language === language);

  return errors.value.translations[translationIndex]?.title;
};

const {
  data: outputAdvertisement,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    name: string().required(t('validation.notEmpty')),
    company: string().nullable().required(t('validation.notEmpty')),
    showInApp: boolean().required(t('validation.notEmpty')),
    showInForBusiness: boolean().required(t('validation.notEmpty')),
    validityStart: date().nullable(),
    validityEnd: date().nullable(),
    places: array().required(t('validation.notEmpty')),
    translations: array()
      .of(
        object({
          title: string().required(t('validation.notEmpty')).max(MAX_TITLE_LENGTH, t('validation.maxLength')),
          description: string().nullable().max(MAX_DESCRIPTION_LENGTH, t('validation.maxLength')),
          url: string().nullable(),
        }),
      )
      .test('has-complete-translation', t('validation.notEmpty'), (translations) => translations.every((t) => !!t.title)),
  }),
  rawAdvertisement,
);

const resetData = () => {
  rawAdvertisement.value = initialData(props.advertisement);
  setValidation(false);
};

const closeModal = () => {
  resetData();
  internalOpen.value = false;
  internalAdvertisement.value = undefined;
};

const [exec, { loading: saving }] = useFetchAction();

const loading = computed(() => saving.value || false.value);

const save = validatedFn(async () => {
  if (!outputAdvertisement.value || isSubmitting.value) {
    return;
  }

  try {
    isSubmitting.value = true;

    const edit = !!props.advertisement;
    const url = edit ? props.advertisement['@id'] : '/advertisements';

    const placesPayload = outputAdvertisement.value.places.map((p) => ({
      spot: p.value,
      id: p['@id'] ?? undefined,
    }));

    const imageUpload = await Promise.all(
      outputAdvertisement.value.translations.map(async (trans) => {
        let uploaded = trans.image;
        if (uploaded?.isNew) {
          uploaded = Upload.from(await uploaded.upload('/files'));
        }
        return { ...trans, image: uploaded };
      }),
    );
    outputAdvertisement.value.translations = imageUpload;

    const translationsPayload = (outputAdvertisement.value.translations as AdvertisementTranslation[])
      .filter((trans) => trans.title || trans.description)
      .map((trans) => ({
        ...trans,
        image: trans.image?.file?.['@id'] ?? null,
        id: trans['@id'] ?? undefined,
      }));

    const savedAdvertisement = await exec<Advertisement>(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: outputAdvertisement.value.name,
        company: outputAdvertisement.value.company,
        showInApp: outputAdvertisement.value.showInApp,
        showInForBusiness: outputAdvertisement.value.showInForBusiness,
        validityStart: outputAdvertisement.value.validityStart,
        validityEnd: outputAdvertisement.value.validityEnd,
        places: placesPayload,
        regions: outputAdvertisement.value.regions,
        translations: translationsPayload,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved', savedAdvertisement.id);
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  } finally {
    isSubmitting.value = false;
  }
});
</script>

<style scoped></style>

